import { useCustomerTemplate, useInactivatedFeatures } from '@domains/subscription/priv/api/queries'
import { type ConfirmableProps, ConfirmModal, type FieldChange } from '@domains/subscription/priv/components/Confirmable'
import {
  useDeleteFeatureForCustomer,
  useEffectiveFeaturesForCustomer,
  useOverriddenFeaturesForCustomer,
  useSaveFeatureForCustomer,
} from '@domains/subscription/priv/Features/api/queries'
import { FeatureForm as FeatureFormComponent, type FeatureFormProps } from '@domains/subscription/priv/Features/FeatureForm'
import { Features } from '@domains/subscription/priv/Features/Features'
import type { FeatureRequest, FieldRequest } from '@domains/subscription/priv/types'
import { createCustomTheme } from '@domains/subscription/theme'
import { Stack, ThemeProvider } from '@mui/material'
import useMaterialNotification from 'hooks/useMaterialNotification'
import type React from 'react'
import { useCallback, useState } from 'react'

interface FeatureSettingsTabProps {
  customerId: string
}

type FormFieldRequest = FieldRequest<null>

export const FeatureSettingsTabFactory = ({ customerId }: FeatureSettingsTabProps) => {
  const [selectedFeatureId, setSelectedFeatureId] = useState<string | undefined>(undefined)

  const { data: template, isLoading: templateIsLoading } = useCustomerTemplate(customerId)
  const {
    data: defaultFeatures = [],
    isLoading: defaultFeaturesIsLoading,
    isFetching: defaultFeaturesIsFetching,
  } = useEffectiveFeaturesForCustomer({ customerId })

  const {
    data: customerFeatures = [],
    isLoading: customerFeaturesIsLoading,
    isFetching: customerFeaturesIsFetching,
  } = useOverriddenFeaturesForCustomer({ customerId })

  const { mutate: mutateDeleteFeature, isLoading: deleteFeatureIsLoading } = useDeleteFeatureForCustomer({ customerId })

  const saveFeatureForCustomer = useSaveFeatureForCustomer(customerId)

  const avaliableFeatures = useInactivatedFeatures(template?.features.map((f) => f.id) ?? [])

  const notify = useMaterialNotification()

  const saveFeature = (featureData: FeatureRequest<null>) => {
    saveFeatureForCustomer.mutate(
      { featureRequest: featureData, featureId: featureData.id },
      {
        onSuccess: () => {
          notify(`${featureData.name} has been saved`)
        },
      },
    )
  }

  const deleteFeature = (featureId: string) => {
    mutateDeleteFeature(
      { featureId },
      {
        onSuccess: () => {
          notify(`Feature has been deleted`)
        },
      },
    )
  }

  const isLoading =
    saveFeatureForCustomer.isLoading ||
    templateIsLoading ||
    customerFeaturesIsLoading ||
    defaultFeaturesIsLoading ||
    defaultFeaturesIsFetching ||
    customerFeaturesIsFetching ||
    deleteFeatureIsLoading

  const ConfirmableComponent = useCallback(
    (props: ConfirmableProps<FormFieldRequest[]>) => {
      if (!template?.numberOfConnections) return null

      return <ConfirmModal {...props} connections={template.numberOfConnections} />
    },
    [template?.numberOfConnections],
  )

  const FeatureForm = useCallback(
    (props: FeatureFormProps) => (
      <FeatureFormComponent {...props} Confirmable={showConfirmable(template?.numberOfConnections, ConfirmableComponent)} />
    ),
    [ConfirmableComponent, template?.numberOfConnections],
  )

  return (
    <ThemeProvider theme={createCustomTheme()}>
      <Stack
        sx={{
          paddingBottom: 2,
        }}
      >
        <Features
          availableFeatures={avaliableFeatures}
          activeFeatures={customerFeatures}
          defaultFeatures={defaultFeatures}
          onDelete={deleteFeature}
          onSave={saveFeature}
          isLoading={isLoading}
          selectedFeatureId={selectedFeatureId}
          onSelectFeature={setSelectedFeatureId}
          isFormDirty={deepEqualFormDirty}
          allowDeleteFeature
          slots={{ FeatureForm }}
        />
      </Stack>
    </ThemeProvider>
  )
}

const showConfirmable = <T,>(numberOfConnections: number | undefined, Component: React.ComponentType<T>) => {
  if (numberOfConnections === undefined || numberOfConnections <= 0) {
    return undefined
  }

  return (props: T) => <Component {...props} connections={numberOfConnections} />
}

const deepEqualFormDirty = (change: FieldChange) => {
  return JSON.stringify(change.before) !== JSON.stringify(change.after)
}
